/**
 * Michigan State-Specific Diagnostic Questions
 * 
 * CRITICAL REGULATORY FRAMEWORK:
 * Michigan does NOT have state licensure for home health agencies.
 * From LARA website: "State licensing is not required for home health agencies."
 * 
 * Home health agencies may voluntarily become Medicare/Medicaid certified through
 * LARA Bureau of Survey and Certification, which conducts surveys on behalf of CMS.
 * 
 * Applicable Regulations:
 * - 42 CFR Part 484: Federal Medicare Conditions of Participation (for Medicare/Medicaid certification)
 * - MCL 333.20173a: Criminal Background Check Requirements (ICHAT + FBI fingerprint)
 * - MCL 400.11a: Adult Protective Services - Mandated Reporter Requirements
 * - MCL 722.623: Child Protection Law - Mandated Reporter Requirements
 * - MCL 333.17201: Nursing License Requirements
 * - 29 CFR 1910.1030: OSHA Bloodborne Pathogens Standard
 * - 45 CFR 164: HIPAA Privacy and Security Rules
 * 
 * Key Agencies:
 * - LARA Bureau of Survey and Certification: LARA-BSCSupport@michigan.gov
 * - Child/Adult Abuse Hotline: (855) 444-3911
 * - LARA General Information: (517) 335-1980
 * - Michigan Board of Nursing: (517) 335-0918
 * - Michigan State Police (ICHAT Help Desk): (517) 241-0606
 * 
 * NOTE: The previous version of this file incorrectly cited "R 325.13101" through
 * "R 325.13158" as "Michigan Home Health Agency Licensing Rules." These regulations
 * DO NOT EXIST for home health agencies. R 325.1901 series applies to Homes for the
 * Aged, not home health agencies.
 */

import type { DiagnosticQuestionConfig } from '../../../types/diagnostic';

export const MICHIGAN_QUESTIONS: DiagnosticQuestionConfig[] = [
  // ============================================
  // SECTION A: CERTIFICATION & OPERATIONS
  // ============================================
  {
    id: 'MI-A1',
    itemId: 'A1',
    stateCode: 'MI',
    title: 'Medicare/Medicaid Certification Status',
    description: 'Michigan does not license home health agencies at the state level. Agencies may voluntarily become Medicare/Medicaid certified through LARA Bureau of Survey and Certification.',
    severity: 'high',
    regulationCode: '42 CFR 484',
    regulationName: 'Certification',
    agencyName: 'Centers for Medicare & Medicaid Services / Michigan LARA',
    agencyAbbr: 'CMS/LARA',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.michigan.gov/lara/bureau-list/bhs/health-facility/home-health-agency',
    questions: [
      {
        id: 'q1',
        question: 'What is your agency\'s certification status?',
        inputType: 'select',
        options: [
          { value: 'medicare_medicaid', label: 'Medicare and Medicaid certified' },
          { value: 'medicare_only', label: 'Medicare certified only' },
          { value: 'pending', label: 'Certification pending' },
          { value: 'not_certified', label: 'Not certified (private pay only)' },
        ],
        required: true,
        helpText: 'Michigan does not require state licensure for home health agencies. Medicare certification is voluntary but required to bill Medicare/Medicaid.',
      },
      {
        id: 'q2',
        question: 'Do you have deemed status from a CMS-approved accrediting organization?',
        inputType: 'select',
        options: [
          { value: 'joint_commission', label: 'Yes - The Joint Commission' },
          { value: 'chap', label: 'Yes - CHAP' },
          { value: 'achc', label: 'Yes - ACHC' },
          { value: 'state_survey', label: 'No - State survey agency (LARA)' },
          { value: 'not_applicable', label: 'Not applicable (not seeking certification)' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'not_certified',
        },
      },
      {
        id: 'q3',
        question: 'When does your current certification/accreditation expire?',
        inputType: 'date',
        helpText: 'Found on your deemed status letter or CMS certification',
        showCondition: {
          questionId: 'q1',
          operator: 'includes',
          value: ['medicare_medicaid', 'medicare_only'],
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'not_certified', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'pending', result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 120, result: 'COMPLIANT' },
      { condition: 'daysFuture', questionId: 'q3', value: 60, result: 'AT_RISK' },
      { condition: 'daysFuture', questionId: 'q3', value: 0, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your agency\'s certification status is appropriate for services provided.',
      atRisk: 'Your certification expires within 120 days. Contact your accrediting organization or LARA at LARA-BSCSupport@michigan.gov to begin the renewal process.',
      nonCompliant: 'Your certification has expired or you are providing Medicare/Medicaid services without proper certification. Contact LARA Bureau of Survey and Certification immediately.',
      unknown: 'Verify your certification status with LARA at (517) 335-1980 or LARA-BSCSupport@michigan.gov.',
    },
    stateNotes: [
      'Michigan does NOT license home health agencies at the state level',
      'Medicare/Medicaid certification is voluntary but required to bill these programs',
      'Private pay agencies do not require any state licensure',
      'LARA conducts certification surveys on behalf of CMS',
      'Deemed status from JCAHO, CHAP, or ACHC satisfies certification survey requirements',
      'To apply for certification, complete CMS-855A and submit to Medicare Administrative Contractor (MAC)',
    ],
  },

  {
    id: 'MI-A2',
    itemId: 'A2',
    stateCode: 'MI',
    title: 'Quality Assessment and Performance Improvement (42 CFR 484.65)',
    description: 'Medicare-certified agencies must maintain a QAPI program',
    severity: 'high',
    regulationCode: '42 CFR 484.65',
    regulationName: 'QAPI',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Does your agency have a formal Quality Assessment and Performance Improvement (QAPI) program?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which QAPI components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'data_collection', label: 'Systematic data collection and analysis' },
          { value: 'performance_indicators', label: 'Performance improvement indicators' },
          { value: 'outcome_measures', label: 'Patient outcome measures' },
          { value: 'action_plans', label: 'Performance improvement action plans' },
          { value: 'governing_body', label: 'Governing body oversight and approval' },
          { value: 'infection_surveillance', label: 'Infection control surveillance' },
          { value: 'adverse_events', label: 'Adverse event tracking and analysis' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 6, result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q2', value: 4, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q2', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'QAPI program meets 42 CFR 484.65 requirements.',
      atRisk: 'QAPI program is missing some required components. Review 42 CFR 484.65 for all required elements.',
      nonCompliant: 'No QAPI program in place. Medicare-certified agencies must maintain a QAPI program per 42 CFR 484.65.',
      unknown: 'Verify QAPI program meets federal requirements.',
    },
    stateNotes: [
      'MCL 333.20101 incorporates federal Medicare standards for Michigan providers',
      'R 325.20101 references CMS Conditions of Participation for certified agencies',
      'QAPI program required for Medicare/Medicaid certification per 42 CFR 484.65',
      'LARA Bureau of Survey and Certification reviews QAPI during certification surveys',
    ],
  },

  {
    id: 'MI-A3',
    itemId: 'A3',
    stateCode: 'MI',
    title: 'Emergency Preparedness (42 CFR 484.102)',
    description: 'Medicare-certified agencies must have an emergency preparedness program',
    severity: 'medium',
    regulationCode: '42 CFR 484.102',
    regulationName: 'Emergency Preparedness',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a documented emergency preparedness plan?',
        inputType: 'select',
        options: [
          { value: 'comprehensive', label: 'Yes, comprehensive written plan reviewed annually' },
          { value: 'partial', label: 'Partial plan, needs updating' },
          { value: 'no', label: 'No emergency preparedness plan' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which emergency preparedness elements does your plan include? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'risk_assessment', label: 'Risk assessment based on hazards likely in Michigan' },
          { value: 'policies', label: 'Emergency policies and procedures' },
          { value: 'communication', label: 'Communication plan with contact information' },
          { value: 'patient_tracking', label: 'Patient tracking system during emergencies' },
          { value: 'training', label: 'Staff training and testing program' },
          { value: 'coordination', label: 'Coordination with local emergency management' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'notEquals',
          value: 'no',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'comprehensive' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Emergency preparedness program meets 42 CFR 484.102 requirements.',
      atRisk: 'Emergency preparedness plan is incomplete. Review 42 CFR 484.102 for required elements.',
      nonCompliant: 'No emergency preparedness plan. Medicare-certified agencies must maintain an emergency preparedness program per 42 CFR 484.102.',
      unknown: 'Review emergency preparedness program against federal requirements.',
    },
    stateNotes: [
      'MCL 333.20101 requires agencies to maintain safe operations including emergencies',
      'R 325.20101-20199 incorporates federal emergency preparedness requirements',
      'Plans must address Michigan-specific hazards: severe winter weather, power outages, floods',
      'Annual review and staff training on emergency procedures required per CMS',
    ],
  },

  {
    id: 'MI-A4',
    itemId: 'A4',
    stateCode: 'MI',
    title: 'Policies and Procedures (42 CFR 484.50)',
    description: 'Home health agencies must maintain written policies governing services',
    severity: 'medium',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following required policies does your agency have documented? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'patient_rights', label: 'Patient rights and responsibilities' },
          { value: 'admission_discharge', label: 'Admission, transfer, and discharge policies' },
          { value: 'personnel', label: 'Personnel policies and job descriptions' },
          { value: 'supervision', label: 'Clinical supervision procedures' },
          { value: 'complaint', label: 'Complaint and grievance procedures' },
          { value: 'abuse_reporting', label: 'Abuse reporting procedures (MCL 400.11a / MCL 722.623)' },
          { value: 'infection_control', label: 'Infection control program' },
          { value: 'emergency', label: 'Emergency preparedness plan' },
          { value: 'quality', label: 'Quality improvement program' },
          { value: 'privacy', label: 'HIPAA privacy and security policies' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 10, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 7, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 7, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your policy manual meets required standards.',
      atRisk: 'Your policy manual is missing some required sections. Review 42 CFR Part 484 for Medicare CoP requirements.',
      nonCompliant: 'Multiple required policies are missing. Immediately develop written policies for all required areas.',
      unknown: 'Review your policies against 42 CFR Part 484 requirements.',
    },
    stateNotes: [
      'MCL 333.20101 requires written policies for patient care and operations',
      'R 325.20101-20199 mandates policies align with federal Medicare standards',
      'LARA reviews policies during certification surveys for completeness',
      'MCL 400.11a and MCL 722.623 require abuse reporting policies specifically',
    ],
  },

  {
    id: 'MI-A5',
    itemId: 'A5',
    stateCode: 'MI',
    title: 'Record Retention (42 CFR 484.110)',
    description: 'Clinical records must be retained per federal requirements',
    severity: 'medium',
    regulationCode: '42 CFR 484.110',
    regulationName: 'Clinical Records',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'How long do you retain patient and employee records?',
        inputType: 'select',
        options: [
          { value: '7plus', label: '7 years or longer' },
          { value: '5to6', label: '5-6 years' },
          { value: 'less5', label: 'Less than 5 years' },
          { value: 'unsure', label: 'Not sure / no formal policy' },
        ],
        required: true,
        helpText: 'Federal CoPs require retention for 5 years after discharge; longer may be required for Medicare billing records',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: '7plus', result: 'COMPLIANT' },
      { condition: 'equals', value: '5to6', result: 'COMPLIANT' },
      { condition: 'includes', value: ['less5', 'unsure'], result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Your record retention policy meets federal requirements.',
      atRisk: 'Review record retention requirements under 42 CFR 484.110.',
      nonCompliant: 'Your record retention does not meet federal requirements. Update your retention policy immediately.',
      unknown: 'Review record retention requirements under 42 CFR 484.110.',
    },
    stateNotes: [
      'MCL 333.20101 requires maintenance of complete patient records',
      'R 325.20101-20199 incorporates federal record retention standards',
      '42 CFR 484.110 requires minimum 5 years retention after discharge',
      'Medicare billing records may require longer retention per CMS guidelines',
    ],
  },

  // ============================================
  // SECTION B: BACKGROUND CHECKS
  // ============================================
  {
    id: 'MI-B1',
    itemId: 'B1',
    stateCode: 'MI',
    title: 'Criminal Background Check Requirements (MCL 333.20173a)',
    description: 'Michigan requires comprehensive background checks for all direct care staff in covered facilities including home health agencies',
    severity: 'high',
    regulationCode: 'MCL 333.20173a',
    regulationName: 'Background Checks',
    agencyName: 'Michigan Department of Licensing and Regulatory Affairs',
    agencyAbbr: 'LARA',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.michigan.gov/lara',
    questions: [
      {
        id: 'q1',
        question: 'For ALL currently employed direct care staff, which of the following do you have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'ichat', label: 'ICHAT (Internet Criminal History Access Tool) check' },
          { value: 'fbi', label: 'FBI fingerprint-based background check' },
          { value: 'registry', label: 'Michigan Nurse Aide Registry verification' },
          { value: 'sex_offender', label: 'Michigan Sex Offender Registry check' },
          { value: 'oig', label: 'OIG/SAM exclusion list verification' },
        ],
        required: true,
        helpText: 'MCL 333.20173a requires both ICHAT and FBI fingerprint checks before patient contact',
      },
      {
        id: 'q2',
        question: 'Do any employees begin working with patients BEFORE their background check clearance is received?',
        inputType: 'binary',
        options: [
          { value: 'no', label: 'No - never before clearance' },
          { value: 'yes', label: 'Yes - sometimes they start early' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you use the Michigan Workforce Background Check system for ongoing monitoring?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        helpText: 'LARA maintains automated notification if subsequent criminal activity is detected',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'no' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 2, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 2, result: 'NON_COMPLIANT' },
      // Not using Michigan Workforce Background Check ongoing monitoring
      { condition: 'equals', questionId: 'q3', value: 'no', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Background check documentation is complete per MCL 333.20173a.',
      atRisk: 'Background check documentation may be incomplete or not using Michigan Workforce Background Check ongoing monitoring. Michigan requires comprehensive checks BEFORE any patient contact.',
      nonCompliant: 'Staff are working without proper background clearance or are starting work before clearance is received. Employees working before background check clearance is a direct violation of MCL 333.20173a. Immediately complete required background checks for all direct care staff.',
      unknown: 'Audit all staff files for background check documentation per MCL 333.20173a.',
    },
    stateNotes: [
      'MCL 333.20173a applies to home health agencies as "covered facilities"',
      'Both ICHAT and FBI fingerprint checks required',
      'Conditional employment allowed while awaiting FBI results if ICHAT is clear',
      'Disqualifying offenses include crimes under 42 USC 1320a-7 and state law',
      'Fingerprints stored for automated notification of subsequent criminal activity',
      'LARA maintains electronic web-based system for background check management',
    ],
  },

  {
    id: 'MI-B2',
    itemId: 'B2',
    stateCode: 'MI',
    title: 'ICHAT Criminal History Check',
    description: 'ICHAT (Internet Criminal History Access Tool) required for all direct care staff',
    severity: 'high',
    regulationCode: 'MCL 333.20173a',
    regulationName: 'ICHAT',
    agencyName: 'Michigan State Police',
    agencyAbbr: 'MSP',
    agencyPhone: '(517) 241-0606',
    agencyUrl: 'https://apps.michigan.gov/ichat/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care staff have an ICHAT criminal history check on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have ICHAT check' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not sure what ICHAT is' },
        ],
        required: true,
        helpText: 'ICHAT is the Internet Criminal History Access Tool - Michigan\'s state-level background check',
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have ICHAT criminal history checks per MCL 333.20173a.',
      atRisk: 'Some staff are missing ICHAT checks. Complete checks immediately - required before patient contact.',
      nonCompliant: 'ICHAT criminal history checks are required per MCL 333.20173a. Access ICHAT at apps.michigan.gov/ichat.',
      unknown: 'Verify all staff have ICHAT checks on file.',
    },
    stateNotes: [
      'MCL 333.20173a requires ICHAT check for all direct care employees',
      'R 325.20101-20199 incorporates background check requirements for home health',
      'ICHAT accessed at apps.michigan.gov/ichat - fee applies per search',
      'ICHAT must be completed before FBI fingerprint results are received',
    ],
  },

  {
    id: 'MI-B3',
    itemId: 'B3',
    stateCode: 'MI',
    title: 'FBI Fingerprint Background Check',
    description: 'FBI fingerprint-based national criminal history check required',
    severity: 'high',
    regulationCode: 'MCL 333.20173a',
    regulationName: 'FBI Check',
    agencyName: 'Michigan State Police',
    agencyAbbr: 'MSP',
    agencyPhone: '(517) 241-0606',
    agencyUrl: 'https://www.michigan.gov/msp',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your direct care staff have FBI fingerprint-based background checks on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all have FBI fingerprint checks' },
          { value: 'most', label: 'Most do, some are pending' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have FBI fingerprint checks per MCL 333.20173a.',
      atRisk: 'Some FBI fingerprint checks may be pending. Staff may work conditionally if ICHAT is clear.',
      nonCompliant: 'FBI fingerprint checks are required by Michigan law.',
      unknown: 'Verify FBI fingerprint check status for all staff.',
    },
    stateNotes: [
      'Fingerprints submitted to MSP and FBI for national check',
      'Fingerprints retained for ongoing monitoring',
      'Conditional employment allowed while awaiting FBI results if ICHAT clear',
      'MSP notifies LARA of any subsequent criminal activity',
    ],
  },

  {
    id: 'MI-B4',
    itemId: 'B4',
    stateCode: 'MI',
    title: 'Nurse Aide Registry Verification',
    description: 'CNAs and HHAs must be verified on the Michigan Nurse Aide Registry',
    severity: 'high',
    regulationCode: '42 CFR 483.156',
    regulationName: 'Nurse Aide Registry',
    agencyName: 'Michigan Department of Licensing and Regulatory Affairs',
    agencyAbbr: 'LARA',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.michigan.gov/lara/bureau-list/bhs/health-facility/nurse-aide-registry',
    questions: [
      {
        id: 'q1',
        question: 'Do you verify registry status for all CNAs/HHAs before hire?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always verify before hire' },
          { value: 'sometimes', label: 'Sometimes' },
          { value: 'no', label: 'No / not sure how to check' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'always', result: 'COMPLIANT' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'equals', value: 'sometimes', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'Registry verification is conducted for all CNAs/HHAs.',
      atRisk: 'Registry verification is inconsistent. Verify all aides before patient contact.',
      nonCompliant: 'Registry verification is not being conducted. Check the Michigan Nurse Aide Registry before hire.',
      unknown: 'Verify registry check procedures are in place.',
    },
  },

  {
    id: 'MI-B5',
    itemId: 'B5',
    stateCode: 'MI',
    title: 'OIG/SAM Exclusion List Verification',
    description: 'Federal exclusion list verification required for all staff',
    severity: 'high',
    regulationCode: '42 CFR 1001',
    regulationName: 'Exclusion List',
    agencyName: 'Office of Inspector General',
    agencyAbbr: 'OIG',
    agencyPhone: '(800) 447-8477',
    agencyUrl: 'https://exclusions.oig.hhs.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you check OIG/SAM exclusion lists for all staff?',
        inputType: 'select',
        options: [
          { value: 'monthly', label: 'Yes, at hire and monthly' },
          { value: 'hire_only', label: 'At hire only' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'monthly', result: 'COMPLIANT' },
      { condition: 'equals', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'OIG/SAM exclusion checks are conducted at hire and monthly.',
      atRisk: 'OIG recommends monthly verification, not just at hire.',
      nonCompliant: 'OIG/SAM exclusion checks are not being conducted. This creates significant liability.',
      unknown: 'Verify OIG/SAM procedures are in place.',
    },
    stateNotes: [
      'MCL 333.20101 prohibits employment of excluded individuals in health care',
      'R 325.20101-20199 requires verification against federal exclusion lists',
      'OIG LEIE available free at exclusions.oig.hhs.gov',
      'SAM.gov exclusion list also required for federal program compliance',
    ],
  },

  // ============================================
  // SECTION C: PERSONNEL FILES & CREDENTIALS
  // ============================================
  {
    id: 'MI-C1',
    itemId: 'C1',
    stateCode: 'MI',
    title: 'Personnel File Requirements (42 CFR 484.80)',
    description: 'Required personnel file documentation for home health agency staff',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'HHA Services',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which of the following do ALL your employees have on file? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'application', label: 'Employment application' },
          { value: 'photo_id', label: 'Government-issued photo ID copy' },
          { value: 'license', label: 'Professional license verification (if applicable)' },
          { value: 'job_description', label: 'Signed job description' },
          { value: 'hipaa', label: 'Signed HIPAA/confidentiality agreement' },
          { value: 'background', label: 'ICHAT and FBI background check results' },
          { value: 'health_clearance', label: 'Health clearance/physical exam' },
          { value: 'tb', label: 'TB screening results' },
          { value: 'training', label: 'Training/competency documentation' },
          { value: 'performance', label: 'Performance evaluations' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 9, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 6, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 6, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Personnel file documentation is complete.',
      atRisk: 'Some personnel files are missing required documentation. Audit all files for completeness.',
      nonCompliant: 'Personnel files are significantly incomplete. This will result in survey deficiencies.',
      unknown: 'Audit personnel files for required documentation.',
    },
    stateNotes: [
      'MCL 333.20173a requires specific background check documentation in files',
      'R 325.20101-20199 mandates personnel files contain training and competency records',
      '42 CFR 484.80 specifies HHA personnel file requirements for Medicare agencies',
      'LARA surveyors review random sample of personnel files during certification surveys',
    ],
  },

  {
    id: 'MI-C2',
    itemId: 'C2',
    stateCode: 'MI',
    title: 'Nursing License Verification (MCL 333.17201)',
    description: 'All nurses must have current Michigan nursing licenses',
    severity: 'high',
    regulationCode: 'MCL 333.17201',
    regulationName: 'Nursing License',
    agencyName: 'Michigan Board of Nursing',
    agencyAbbr: 'LARA',
    agencyPhone: '(517) 335-0918',
    agencyUrl: 'https://www.michigan.gov/lara/bureau-list/bpl/health/nursing',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your nurses have current Michigan nursing licenses verified?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, verify before hire and track renewals' },
          { value: 'hire_only', label: 'Verify at hire only' },
          { value: 'no', label: 'No formal verification process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you accept Nurse Licensure Compact (NLC) multistate licenses?',
        inputType: 'select',
        options: [
          { value: 'yes_verify', label: 'Yes, with verification of compact state license' },
          { value: 'mi_only', label: 'No, Michigan license required' },
          { value: 'unsure', label: 'Not sure of our policy' },
        ],
        helpText: 'Michigan is NOT currently a member of the NLC - Michigan license required',
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'hire_only', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Professional license verification process meets requirements.',
      atRisk: 'License verification at hire only is insufficient. Implement ongoing verification.',
      nonCompliant: 'No license verification process creates significant risk.',
      unknown: 'Verify license verification procedures are in place.',
    },
    stateNotes: [
      'Michigan is NOT currently a member of the Nurse Licensure Compact (NLC)',
      'All nurses must hold a valid Michigan nursing license',
      'Verify licenses at michigan.gov/lara',
      'RNs, LPNs, and APRNs must maintain current Michigan license',
    ],
  },

  // ============================================
  // SECTION D: TRAINING & COMPETENCY
  // ============================================
  {
    id: 'MI-D1',
    itemId: 'D1',
    stateCode: 'MI',
    title: 'Home Health Aide Training (42 CFR 484.80)',
    description: 'Home health aides must complete 75 hours of training per federal requirements',
    severity: 'high',
    regulationCode: '42 CFR 484.80',
    regulationName: 'HHA Services',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your Home Health Aides have documented completion of a 75-hour training program?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all HHAs have completed approved training' },
          { value: 'most', label: 'Most have completed training, some are pending' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'HHAs must complete 75-hour training including at least 16 hours of supervised practical training',
      },
      {
        id: 'q2',
        question: 'Do HHAs complete annual in-service training of at least 12 hours?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q3',
        question: 'Do you complete competency evaluations for HHAs?',
        inputType: 'select',
        options: [
          { value: 'initial_annual', label: 'Yes, at hire and annually' },
          { value: 'initial_only', label: 'At hire only' },
          { value: 'no', label: 'No formal evaluations' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
        { condition: 'equals', questionId: 'q3', value: 'initial_annual' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q3', value: 'initial_only' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HHA training program meets 42 CFR 484.80 requirements.',
      atRisk: 'HHA training program has gaps. Ensure all HHAs complete required training and annual competency evaluations.',
      nonCompliant: 'HHAs are providing care without proper training. This violates 42 CFR 484.80.',
      unknown: 'Verify HHA training documentation meets federal requirements.',
    },
    stateNotes: [
      '75 hours initial training required (minimum 16 hours supervised practical)',
      '12 hours annual in-service training required',
      'Annual competency evaluation required on 13 key subject areas',
      'Training must be provided by or under supervision of RN',
      'CNAs meeting federal requirements may work as HHAs',
    ],
  },

  {
    id: 'MI-D2',
    itemId: 'D2',
    stateCode: 'MI',
    title: 'Mandated Reporter Training - Adult Abuse (MCL 400.11a)',
    description: 'Michigan mandated reporter training required for adult abuse recognition and reporting',
    severity: 'high',
    regulationCode: 'MCL 400.11a',
    regulationName: 'APS Reporting',
    agencyName: 'Michigan Department of Health and Human Services',
    agencyAbbr: 'MDHHS',
    agencyPhone: '(855) 444-3911',
    agencyUrl: 'https://www.michigan.gov/mdhhs/adult-child-serv/abuse-neglect/aps',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff receive training on mandated reporter responsibilities for adult abuse?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff are trained' },
          { value: 'most', label: 'Most staff are trained' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do staff know how to report suspected adult abuse, neglect, or exploitation?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes - call (855) 444-3911' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff are trained on adult abuse mandated reporter duties per MCL 400.11a.',
      atRisk: 'Some staff may lack mandated reporter training. Complete training for all staff.',
      nonCompliant: 'Mandated reporter training is required. Health care workers must report suspected abuse to (855) 444-3911.',
      unknown: 'Verify all staff have received mandated reporter training per MCL 400.11a.',
    },
    stateNotes: [
      'Health care workers are mandated reporters under MCL 400.11a',
      'Must report suspected abuse, neglect, or exploitation of vulnerable adults',
      'Immediate oral report required to county DSS or centralized intake',
      'Report to Adult Protective Services: (855) 444-3911',
      'Written report may be filed after oral report',
    ],
  },

  {
    id: 'MI-D3',
    itemId: 'D3',
    stateCode: 'MI',
    title: 'Mandated Reporter Training - Child Abuse (MCL 722.623)',
    description: 'Michigan mandated reporter training for child abuse recognition and reporting',
    severity: 'high',
    regulationCode: 'MCL 722.623',
    regulationName: 'CPS Reporting',
    agencyName: 'Michigan Department of Health and Human Services',
    agencyAbbr: 'MDHHS',
    agencyPhone: '(855) 444-3911',
    agencyUrl: 'https://www.michigan.gov/mdhhs/adult-child-serv/abuse-neglect/childrens/mandated-reporters',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your staff have documented child abuse mandated reporter training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have completed training' },
          { value: 'most', label: 'Most have it, some are missing' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'equals', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'All staff have completed child abuse mandated reporter training per MCL 722.623.',
      atRisk: 'Some staff are missing mandated reporter training. Ensure all staff complete training.',
      nonCompliant: 'Child abuse mandated reporter training is required. Report suspected abuse to (855) 444-3911.',
      unknown: 'Verify all staff have completed mandated reporter training per MCL 722.623.',
    },
    stateNotes: [
      'Health care workers are mandated reporters under MCL 722.623',
      'Immediate oral report required to Children\'s Protective Services',
      'Written report within 72 hours',
      'Report to Centralized Intake: (855) 444-3911',
      'Failure to report is a misdemeanor per MCL 722.633',
    ],
  },

  {
    id: 'MI-D4',
    itemId: 'D4',
    stateCode: 'MI',
    title: 'HIPAA Training (45 CFR 164.530)',
    description: 'HIPAA requires documented privacy and security training',
    severity: 'high',
    regulationCode: '45 CFR 164.530',
    regulationName: 'HIPAA Training',
    agencyName: 'HHS Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL employees receive documented HIPAA training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all employees with documented completion' },
          { value: 'most', label: 'Most employees, some gaps exist' },
          { value: 'no', label: 'No formal HIPAA training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'When do employees receive HIPAA training?',
        inputType: 'checklist',
        options: [
          { value: 'hire', label: 'At hire (within reasonable time)' },
          { value: 'annual', label: 'Annual refresher training' },
          { value: 'changes', label: 'When policies change' },
          { value: 'breach', label: 'Following any privacy incident' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'HIPAA training is documented for all employees per 45 CFR 164.530.',
      atRisk: 'HIPAA training documentation has gaps. Complete training for all employees.',
      nonCompliant: 'HIPAA training is not being provided. This violates 45 CFR 164.530.',
      unknown: 'Verify HIPAA training documentation.',
    },
    stateNotes: [
      'MCL 333.20101 requires patient privacy protections aligned with federal law',
      'R 325.20101-20199 incorporates HIPAA requirements for Michigan providers',
      '45 CFR 164.530 requires training within reasonable time of hire',
      'Annual refresher training recommended per HHS Office for Civil Rights',
    ],
  },

  {
    id: 'MI-D5',
    itemId: 'D5',
    stateCode: 'MI',
    title: 'Infection Control Training (42 CFR 484.70)',
    description: 'Infection prevention and control training required for all clinical staff',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL clinical staff receive documented infection control training?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff with documented training' },
          { value: 'most', label: 'Most staff, some gaps exist' },
          { value: 'no', label: 'No formal infection control training' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Which infection control topics are covered? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'hand_hygiene', label: 'Hand hygiene' },
          { value: 'ppe', label: 'Personal protective equipment (PPE)' },
          { value: 'standard_precautions', label: 'Standard precautions' },
          { value: 'transmission', label: 'Transmission-based precautions' },
          { value: 'bloodborne', label: 'Bloodborne pathogen exposure' },
          { value: 'waste', label: 'Medical waste disposal' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'all' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'most' },
        { condition: 'countAtLeast', questionId: 'q2', value: 3 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control training is documented for all staff per 42 CFR 484.70.',
      atRisk: 'Infection control training has gaps. Review training curriculum and staff completion.',
      nonCompliant: 'Infection control training is not being provided. This violates 42 CFR 484.70.',
      unknown: 'Verify infection control training documentation.',
    },
    stateNotes: [
      'MCL 333.20101 requires infection prevention measures for patient safety',
      'R 325.20101-20199 mandates infection control programs for certified agencies',
      '42 CFR 484.70 specifies infection control requirements for Medicare agencies',
      'Training must cover standard precautions, hand hygiene, PPE, and bloodborne pathogens',
    ],
  },

  // ============================================
  // SECTION E: HEALTH & SAFETY
  // ============================================
  {
    id: 'MI-E1',
    itemId: 'E1',
    stateCode: 'MI',
    title: 'TB Screening',
    description: 'TB screening required for all patient care staff',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Control',
    agencyName: 'Michigan Department of Health and Human Services',
    agencyAbbr: 'MDHHS',
    agencyPhone: '(517) 335-8165',
    agencyUrl: 'https://www.michigan.gov/mdhhs/keep-mi-healthy/chronicdiseases/tb',
    questions: [
      {
        id: 'q1',
        question: 'Do ALL your patient care staff have current TB screening on file?',
        inputType: 'select',
        options: [
          { value: 'all', label: 'Yes, all staff have TB screening documentation' },
          { value: 'most', label: 'Most do, some may be missing' },
          { value: 'no', label: 'No / not tracking this' },
        ],
        required: true,
        helpText: 'TB screening must be completed before patient contact',
      },
      {
        id: 'q2',
        question: 'What TB screening method do you use?',
        inputType: 'select',
        options: [
          { value: 'tst', label: 'Tuberculin skin test (TST/PPD)' },
          { value: 'igra', label: 'Blood test (IGRA - QFT or T-SPOT)' },
          { value: 'either', label: 'Either method based on employee preference' },
          { value: 'unsure', label: 'Not sure' },
        ],
      },
    ],
    scoringRules: [
      // 1. NON_COMPLIANT - check for failure condition first
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      // 2. COMPLIANT - check for full compliance
      { condition: 'equals', questionId: 'q1', value: 'all', result: 'COMPLIANT' },
      // 3. AT_RISK - partial compliance / warning state
      { condition: 'equals', questionId: 'q2', value: 'unsure', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'most', result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'TB screening documentation is complete for all staff.',
      atRisk: 'Some TB screenings may be missing. Review all staff files for documentation.',
      nonCompliant: 'Staff are working without TB screening documentation. This violates infection control requirements.',
      unknown: 'Verify TB screening status for all staff.',
    },
    stateNotes: [
      'MCL 333.20101 requires health screenings for patient care staff',
      'R 325.20101-20199 mandates TB screening as part of infection control',
      '42 CFR 484.70 requires TB screening for Medicare-certified agencies',
      'MDHHS TB Program provides guidance at michigan.gov/mdhhs',
    ],
  },

  {
    id: 'MI-E2',
    itemId: 'E2',
    stateCode: 'MI',
    title: 'Hepatitis B Vaccination Offer (29 CFR 1910.1030)',
    description: 'OSHA requires Hepatitis B vaccination be offered to at-risk employees',
    severity: 'medium',
    regulationCode: '29 CFR 1910.1030',
    regulationName: 'Bloodborne Pathogens',
    agencyName: 'Occupational Safety and Health Administration',
    agencyAbbr: 'OSHA',
    agencyPhone: '(800) 321-6742',
    agencyUrl: 'https://www.osha.gov/',
    questions: [
      {
        id: 'q1',
        question: 'Do you offer Hepatitis B vaccination to employees with potential bloodborne pathogen exposure?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, offered to all at-risk employees at no cost' },
          { value: 'some', label: 'Offered to some employees' },
          { value: 'no', label: 'No / not offered' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you document employee acceptance or declination of the vaccine?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'some' },
        { condition: 'equals', questionId: 'q2', value: 'no' },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Hepatitis B vaccination program meets OSHA 29 CFR 1910.1030 requirements.',
      atRisk: 'Hepatitis B vaccination program may be incomplete. Document all offers and declinations.',
      nonCompliant: 'Hepatitis B vaccination is not being offered. This violates 29 CFR 1910.1030.',
      unknown: 'Verify Hepatitis B vaccination procedures are in place.',
    },
    stateNotes: [
      'MCL 333.20101 incorporates federal OSHA requirements for Michigan employers',
      'R 325.20101-20199 mandates bloodborne pathogen protections',
      '29 CFR 1910.1030 requires vaccination offer within 10 days of assignment',
      'Written declination required if employee refuses - keep in personnel file',
    ],
  },

  {
    id: 'MI-E3',
    itemId: 'E3',
    stateCode: 'MI',
    title: 'Infection Control Program (42 CFR 484.70)',
    description: 'Medicare-certified agencies must maintain a comprehensive infection prevention and control program',
    severity: 'high',
    regulationCode: '42 CFR 484.70',
    regulationName: 'Infection Control',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which infection control components does your agency have in place? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'policy', label: 'Written infection control policies and procedures' },
          { value: 'training', label: 'Staff infection control training' },
          { value: 'ppe', label: 'PPE availability and training' },
          { value: 'hand_hygiene', label: 'Hand hygiene protocols' },
          { value: 'surveillance', label: 'Infection surveillance and reporting' },
          { value: 'waste', label: 'Medical waste disposal procedures' },
          { value: 'exposure', label: 'Exposure control plan (OSHA)' },
        ],
        required: true,
      },
    ],
    scoringRules: [
      { condition: 'countAtLeast', value: 7, result: 'COMPLIANT' },
      { condition: 'countAtLeast', value: 5, result: 'AT_RISK' },
      { condition: 'countLessThan', value: 5, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Infection control program meets 42 CFR 484.70 requirements.',
      atRisk: 'Infection control program is missing some required components.',
      nonCompliant: 'Infection control program is inadequate. Review 42 CFR 484.70 requirements.',
      unknown: 'Review infection control program against federal requirements.',
    },
    stateNotes: [
      'MCL 333.20101 requires comprehensive infection prevention for patient safety',
      'R 325.20101-20199 incorporates federal infection control standards',
      '42 CFR 484.70 specifies required infection control program elements',
      'Program must include surveillance, training, PPE, and outbreak response',
    ],
  },

  // ============================================
  // SECTION F: CARE DELIVERY
  // ============================================
  {
    id: 'MI-F1',
    itemId: 'F1',
    stateCode: 'MI',
    title: 'Patient Assessment (42 CFR 484.55)',
    description: 'Comprehensive patient assessments required before services begin',
    severity: 'high',
    regulationCode: '42 CFR 484.55',
    regulationName: 'Assessment',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you complete a comprehensive assessment for EVERY patient within required timeframes?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always within required timeframes' },
          { value: 'usually', label: 'Usually, but sometimes delayed' },
          { value: 'no', label: 'No formal assessment process' },
        ],
        required: true,
        helpText: 'Initial assessment must be completed within 48 hours of referral or within 48 hours of patient\'s return home',
      },
      {
        id: 'q2',
        question: 'Which assessment elements does your agency document? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'physical', label: 'Physical health status' },
          { value: 'functional', label: 'Functional abilities and limitations' },
          { value: 'cognitive', label: 'Cognitive and mental status' },
          { value: 'psychosocial', label: 'Psychosocial status' },
          { value: 'medications', label: 'Medication review and reconciliation' },
          { value: 'nutrition', label: 'Nutritional status' },
          { value: 'home_safety', label: 'Home environment safety' },
          { value: 'caregiver', label: 'Caregiver availability and capability' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient assessments meet 42 CFR 484.55 requirements.',
      atRisk: 'Assessments may be incomplete or delayed. Review 42 CFR 484.55 for required elements and timeframes.',
      nonCompliant: 'No formal assessment process exists. This violates 42 CFR 484.55.',
      unknown: 'Review assessment process against federal requirements.',
    },
    stateNotes: [
      'MCL 333.20101 requires comprehensive patient evaluation before services',
      'R 325.20101-20199 incorporates federal assessment requirements',
      '42 CFR 484.55 requires initial assessment within 48 hours of referral',
      'Assessment must be performed by qualified professional (RN for skilled services)',
    ],
  },

  {
    id: 'MI-F2',
    itemId: 'F2',
    stateCode: 'MI',
    title: 'Plan of Care (42 CFR 484.60)',
    description: 'Written plan of care required for each patient',
    severity: 'high',
    regulationCode: '42 CFR 484.60',
    regulationName: 'Care Planning',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you have a written plan of care for EVERY patient before services begin?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before first visit' },
          { value: 'usually', label: 'Usually, but sometimes services start first' },
          { value: 'no', label: 'No formal care plan process' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do your plans of care include all required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'diagnosis', label: 'Patient diagnoses' },
          { value: 'services', label: 'Types of services to be provided' },
          { value: 'frequency', label: 'Frequency and duration of visits' },
          { value: 'goals', label: 'Measurable patient-specific goals' },
          { value: 'medications', label: 'Medications and treatments' },
          { value: 'physician', label: 'Physician orders/certification' },
          { value: 'update', label: 'Plan review schedule (at least every 60 days)' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 6 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 4 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Plans of care meet 42 CFR 484.60 requirements.',
      atRisk: 'Plans of care may be incomplete or created after services begin.',
      nonCompliant: 'Services are being provided without written plans of care. This violates 42 CFR 484.60.',
      unknown: 'Review plan of care process against federal requirements.',
    },
    stateNotes: [
      'MCL 333.20101 requires individualized care planning for each patient',
      'R 325.20101-20199 mandates care plans per federal Medicare standards',
      '42 CFR 484.60 requires plan before services and review every 60 days',
      'Physician/allowed practitioner must sign orders for skilled services',
    ],
  },

  {
    id: 'MI-F3',
    itemId: 'F3',
    stateCode: 'MI',
    title: 'RN Supervision of Home Health Aides (42 CFR 484.80)',
    description: 'RN supervision required per federal regulations',
    severity: 'high',
    regulationCode: '42 CFR 484.80(h)',
    regulationName: 'HHA Supervision',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide RN supervision of home health aides as required?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, per regulatory schedule' },
          { value: 'partial', label: 'Sometimes, not consistently' },
          { value: 'no', label: 'No supervision program' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How often does RN supervision occur?',
        inputType: 'select',
        options: [
          { value: 'every_14', label: 'At least every 14 days' },
          { value: 'every_30', label: 'Every 30 days' },
          { value: 'less', label: 'Less frequently' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
      {
        id: 'q3',
        question: 'Does the RN make an on-site visit to the patient\'s home during supervision?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q2', value: 'every_14' },
        { condition: 'equals', questionId: 'q3', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'partial' },
        { condition: 'equals', questionId: 'q2', value: 'every_30' },
      ], result: 'AT_RISK' },
    ],
    reportLanguage: {
      compliant: 'RN supervision meets 42 CFR 484.80(h) requirements.',
      atRisk: 'RN supervision may not meet required frequency or on-site visit requirements.',
      nonCompliant: 'No RN supervision program exists. This violates 42 CFR 484.80(h). Implement RN supervision immediately.',
      unknown: 'Review supervision requirements per 42 CFR 484.80(h).',
    },
    stateNotes: [
      'Supervision must occur at least every 14 days',
      'RN must make an on-site visit to patient\'s home',
      'Supervision visit must occur while HHA is providing care',
      'Alternative supervision timing if no HHA services for 14+ days',
    ],
  },

  {
    id: 'MI-F4',
    itemId: 'F4',
    stateCode: 'MI',
    title: 'Clinical Documentation (42 CFR 484.110)',
    description: 'Clinical documentation requirements for home health services',
    severity: 'high',
    regulationCode: '42 CFR 484.110',
    regulationName: 'Clinical Records',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Which documentation elements are completed for EVERY visit? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'date_time', label: 'Date and time of visit' },
          { value: 'services', label: 'Services provided' },
          { value: 'patient_status', label: 'Patient status and observations' },
          { value: 'signature', label: 'Clinician signature' },
          { value: 'care_plan', label: 'Progress toward care plan goals' },
          { value: 'incidents', label: 'Incident documentation (when applicable)' },
          { value: 'communication', label: 'Communication with physician/team' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'How quickly are visit notes completed after the visit?',
        inputType: 'select',
        options: [
          { value: 'same_day', label: 'Same day' },
          { value: 'within_48', label: 'Within 48 hours' },
          { value: 'within_week', label: 'Within one week' },
          { value: 'varies', label: 'Varies / no standard' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 6 },
        { condition: 'includes', questionId: 'q2', value: ['same_day', 'within_48'] },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 4 },
        { condition: 'equals', questionId: 'q2', value: 'within_week' },
      ], result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 4, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Clinical documentation meets 42 CFR 484.110 requirements.',
      atRisk: 'Documentation may be incomplete or delayed. Review documentation practices.',
      nonCompliant: 'Clinical documentation does not meet requirements. Implement comprehensive documentation procedures.',
      unknown: 'Review documentation practices against federal requirements.',
    },
    stateNotes: [
      'MCL 333.20101 requires accurate and complete clinical documentation',
      'R 325.20101-20199 incorporates federal documentation standards',
      '42 CFR 484.110 specifies required elements for clinical records',
      'Documentation should be completed same day or within 48 hours of visit',
    ],
  },

  {
    id: 'MI-F5',
    itemId: 'F5',
    stateCode: 'MI',
    title: 'Patient Rights (42 CFR 484.50)',
    description: 'Patient rights notification and documentation requirements',
    severity: 'high',
    regulationCode: '42 CFR 484.50',
    regulationName: 'Patient Rights',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide EVERY patient with written notice of their rights before or at start of services?',
        inputType: 'select',
        options: [
          { value: 'always', label: 'Yes, always before or at first visit' },
          { value: 'usually', label: 'Usually, but not always documented' },
          { value: 'no', label: 'No / not sure' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Does your patient rights document include required elements? (Select all that apply)',
        inputType: 'checklist',
        options: [
          { value: 'dignity', label: 'Right to dignity and respect' },
          { value: 'participation', label: 'Right to participate in care planning' },
          { value: 'info', label: 'Right to information about services and charges' },
          { value: 'privacy', label: 'Right to privacy and confidentiality' },
          { value: 'complaint', label: 'Right to voice complaints/grievances' },
          { value: 'refuse', label: 'Right to refuse services' },
          { value: 'contact', label: 'State hotline contact information' },
          { value: 'advance_directive', label: 'Advance directive information' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'always' },
        { condition: 'countAtLeast', questionId: 'q2', value: 7 },
      ], result: 'COMPLIANT' },
      { condition: 'anyTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'usually' },
        { condition: 'countAtLeast', questionId: 'q2', value: 5 },
      ], result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Patient rights documentation meets 42 CFR 484.50 requirements.',
      atRisk: 'Patient rights documentation may be incomplete or not consistently provided.',
      nonCompliant: 'Patients are not receiving required rights information. This violates 42 CFR 484.50.',
      unknown: 'Review patient rights process against federal requirements.',
    },
    stateNotes: [
      'Include LARA complaint hotline information',
      'Include information on how to file complaints',
      'Document patient/representative acknowledgment of receipt',
      'Provide in language patient understands',
    ],
  },

  {
    id: 'MI-F6',
    itemId: 'F6',
    stateCode: 'MI',
    title: 'Notice of Privacy Practices (45 CFR 164.520)',
    description: 'HIPAA requires Notice of Privacy Practices to be provided to patients',
    severity: 'high',
    regulationCode: '45 CFR 164.520',
    regulationName: 'Privacy Notice',
    agencyName: 'HHS Office for Civil Rights',
    agencyAbbr: 'OCR',
    agencyPhone: '(800) 368-1019',
    agencyUrl: 'https://www.hhs.gov/hipaa/',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide a Notice of Privacy Practices to all patients?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do patients sign acknowledgment of receiving the Notice of Privacy Practices?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'equals', questionId: 'q1', value: 'yes' },
        { condition: 'equals', questionId: 'q2', value: 'yes' },
      ], result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q1', value: 'yes', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Notice of Privacy Practices is provided and documented per 45 CFR 164.520.',
      atRisk: 'NPP is provided but acknowledgment documentation may be incomplete.',
      nonCompliant: 'Notice of Privacy Practices is not being provided. This violates 45 CFR 164.520.',
      unknown: 'Verify Notice of Privacy Practices procedures.',
    },
    stateNotes: [
      'MCL 333.20101 requires patient privacy protections aligned with HIPAA',
      'R 325.20101-20199 incorporates federal privacy requirements',
      '45 CFR 164.520 requires NPP at first service delivery',
      'Good faith effort to obtain acknowledgment signature required',
    ],
  },

  // ============================================
  // SECTION G: EVV & DOCUMENTATION
  // ============================================
  {
    id: 'MI-G1',
    itemId: 'G1',
    stateCode: 'MI',
    title: 'Electronic Visit Verification (21st Century Cures Act)',
    description: 'EVV required for Medicaid-funded personal care and home health services',
    severity: 'high',
    regulationCode: '21st Century Cures Act',
    regulationName: 'EVV',
    agencyName: 'Michigan Department of Health and Human Services',
    agencyAbbr: 'MDHHS',
    agencyPhone: '(844) 799-9876',
    agencyUrl: 'https://www.michigan.gov/mdhhs',
    questions: [
      {
        id: 'q1',
        question: 'Do you provide Medicaid-funded home health or personal care services?',
        inputType: 'binary',
        options: [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No - private pay or Medicare only' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you use an approved EVV system for Medicaid services?',
        inputType: 'select',
        options: [
          { value: 'yes', label: 'Yes, using state system or approved vendor' },
          { value: 'pending', label: 'Implementation in progress' },
          { value: 'no', label: 'No' },
        ],
        showCondition: {
          questionId: 'q1',
          operator: 'equals',
          value: 'yes',
        },
      },
    ],
    scoringRules: [
      { condition: 'equals', questionId: 'q1', value: 'no', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'yes', result: 'COMPLIANT' },
      { condition: 'equals', questionId: 'q2', value: 'pending', result: 'AT_RISK' },
      { condition: 'equals', questionId: 'q2', value: 'no', result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'EVV system is properly implemented or not required for your services.',
      atRisk: 'Complete EVV implementation for all Medicaid services.',
      nonCompliant: 'Medicaid-funded services require EVV per federal law. Contact MDHHS at (844) 799-9876.',
      unknown: 'Verify EVV compliance if providing Medicaid services.',
    },
    stateNotes: [
      'EVV required for Medicaid personal care services',
      'EVV required for Medicaid home health services requiring in-home visit',
      'Must capture: type of service, individual receiving service, date, location, individual providing service, time service begins and ends',
    ],
  },

  {
    id: 'MI-G2',
    itemId: 'G2',
    stateCode: 'MI',
    title: 'Document Expiration Tracking',
    description: 'System to track license, certification, and clearance expirations',
    severity: 'medium',
    regulationCode: '42 CFR 484.80',
    regulationName: 'Personnel',
    agencyName: 'Centers for Medicare & Medicaid Services',
    agencyAbbr: 'CMS',
    agencyPhone: '(517) 335-1980',
    agencyUrl: 'https://www.ecfr.gov/current/title-42/chapter-IV/subchapter-G/part-484',
    questions: [
      {
        id: 'q1',
        question: 'Do you track expiration dates for all required documents?',
        inputType: 'checklist',
        options: [
          { value: 'certification', label: 'Medicare certification/accreditation' },
          { value: 'insurance', label: 'Liability insurance' },
          { value: 'licenses', label: 'Professional licenses' },
          { value: 'tb', label: 'TB screenings' },
          { value: 'cpr', label: 'CPR/First Aid certifications' },
          { value: 'training', label: 'Annual training due dates' },
        ],
        required: true,
      },
      {
        id: 'q2',
        question: 'Do you receive advance reminders before documents expire?',
        inputType: 'select',
        options: [
          { value: 'auto', label: 'Yes, automated reminders 30+ days in advance' },
          { value: 'manual', label: 'Yes, manual review monthly' },
          { value: 'no', label: 'No proactive tracking' },
        ],
      },
    ],
    scoringRules: [
      { condition: 'allTrue', value: [
        { condition: 'countAtLeast', questionId: 'q1', value: 5 },
        { condition: 'includes', questionId: 'q2', value: ['auto', 'manual'] },
      ], result: 'COMPLIANT' },
      { condition: 'countAtLeast', questionId: 'q1', value: 3, result: 'AT_RISK' },
      { condition: 'countLessThan', questionId: 'q1', value: 3, result: 'NON_COMPLIANT' },
    ],
    reportLanguage: {
      compliant: 'Document expiration tracking is comprehensive with proactive reminders.',
      atRisk: 'Expiration tracking is incomplete. Implement tracking to prevent compliance lapses.',
      nonCompliant: 'No systematic document tracking in place. This creates significant compliance risk.',
      unknown: 'Implement document expiration tracking system.',
    },
    stateNotes: [
      'MCL 333.20173a requires current background check documentation',
      'R 325.20101-20199 mandates current professional licenses and certifications',
      'Medicare certification requires ongoing document currency per CMS',
      'LARA surveyors verify document currency during certification surveys',
    ],
  },
];

export default MICHIGAN_QUESTIONS;